import React from 'react';
import { motion } from 'framer-motion';
import { ArrowTopRightOnSquareIcon, CodeBracketIcon } from '@heroicons/react/24/solid';

// --- Data for the page ---
const setupGuides = [
    {
        name: 'Selenium with Python',
        description: 'The classic combination for powerful, cross-browser web automation.',
        link: 'https://www.selenium.dev/documentation/webdriver/getting_started/',
        steps: [
            'Install Python on your system.',
            'Use pip to install the Selenium library: pip install selenium',
            'Download the correct WebDriver for your browser (e.g., ChromeDriver).',
            'You are ready to write your first script in your favorite code editor.',
        ],
    },
    {
        name: 'Cypress',
        description: 'A modern, all-in-one framework for end-to-end testing of web applications.',
        link: 'https://docs.cypress.io/guides/getting-started/installing-cypress',
        steps: [
            'Install Node.js and npm (Node Package Manager).',
            'In your project folder, run: npm install cypress --save-dev',
            'Open Cypress with: npx cypress open',
            'Cypress will create a sample project structure for you instantly.',
        ],
    },
    {
        name: 'Postman',
        description: 'The industry-standard tool for API testing, from simple requests to complex collections.',
        link: 'https://learning.postman.com/docs/getting-started/introduction/',
        steps: [
            'Download the Postman desktop application from their official website.',
            'Install the application on your Windows, Mac, or Linux machine.',
            'Create a free account to sync your work.',
            'Start sending your first API requests immediately.',
        ],
    },
];

// --- Animation Variants ---
const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

const cardHover = {
    y: -8,
    boxShadow: "0px 15px 25px rgba(0,0,0,0.1)",
    transition: { type: "spring", stiffness: 300 }
};

const AutomationLearningPage = () => {
    return (
        <div className="space-y-24">
            {/* Page Header */}
            <motion.div
                className="text-center"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
                    Dive Into Test Automation
                </h1>
                <p className="mt-4 max-w-3xl mx-auto text-xl text-gray-600">
                    Learn how to set up your environment and write your first automated tests with industry-leading tools.
                </p>
            </motion.div>

            {/* Environment Setup Section */}
            <motion.section
                variants={sectionVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
            >
                <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Environment Setup Guides</h2>
                <motion.div
                    className="grid grid-cols-1 lg:grid-cols-3 gap-8"
                    variants={staggerContainer}
                >
                    {setupGuides.map((guide) => (
                        <motion.div
                            key={guide.name}
                            className="bg-white p-8 rounded-xl shadow-lg flex flex-col border border-gray-200"
                            variants={itemVariants}
                            whileHover={cardHover}
                        >
                            <h3 className="text-2xl font-semibold text-gray-800">{guide.name}</h3>
                            <p className="mt-2 text-gray-600 flex-grow">{guide.description}</p>
                            <ul className="mt-4 space-y-2 text-gray-700 list-decimal list-inside">
                                {guide.steps.map((step, index) => <li key={index}>{step}</li>)}
                            </ul>
                            <a
                                href={guide.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-6 inline-flex items-center font-semibold text-indigo-600 hover:text-indigo-500 self-start group"
                            >
                                View Official Docs
                                <ArrowTopRightOnSquareIcon className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
                            </a>
                        </motion.div>
                    ))}
                </motion.div>
            </motion.section>

            {/* Sample Scripts Section */}
            <motion.section
                variants={sectionVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
            >
                <div className="text-center mb-12">
                    <div className="inline-block bg-indigo-100 p-4 rounded-lg">
                        <CodeBracketIcon className="h-10 w-10 text-indigo-600" />
                    </div>
                    <h2 className="mt-4 text-3xl font-bold text-gray-900">Sample Test Scripts</h2>
                    <p className="mt-3 max-w-2xl mx-auto text-lg text-gray-600">
                        See practical examples of how to automate a simple login form with Selenium and Cypress.
                    </p>
                </div>

                <motion.div
                    className="grid grid-cols-1 lg:grid-cols-2 gap-12"
                    variants={staggerContainer}
                >
                    {/* Selenium with Python Example */}
                    <motion.div variants={itemVariants}>
                        <h3 className="text-2xl font-semibold text-gray-900 mb-4">Selenium with Python</h3>
                        <div className="bg-gray-800 text-white rounded-lg shadow-2xl overflow-hidden">
                            <div className="bg-gray-700 px-4 py-2 text-xs font-mono text-gray-300">example.py</div>
                            <div className="p-4 overflow-x-auto">
                                <pre><code>
{`from selenium import webdriver
from selenium.webdriver.common.by import By
import time

# Initialize the Chrome driver
driver = webdriver.Chrome()

try:
    # 1. Open the website
    driver.get("https://your-demo-website.com/login")

    # 2. Find fields and enter credentials
    driver.find_element(By.ID, "username").send_keys("testuser")
    driver.find_element(By.ID, "password").send_keys("password123")
    
    # 3. Find the login button and click it
    driver.find_element(By.TAG_NAME, "button").click()

    # 4. Wait and verify the result
    time.sleep(2)
    assert "Welcome" in driver.page_source

finally:
    # Close the browser window
    driver.quit()`}
                                </code></pre>
                            </div>
                        </div>
                    </motion.div>

                    {/* Cypress Example */}
                    <motion.div variants={itemVariants}>
                        <h3 className="text-2xl font-semibold text-gray-900 mb-4">Cypress with JavaScript</h3>
                        <div className="bg-gray-800 text-white rounded-lg shadow-2xl overflow-hidden">
                            <div className="bg-gray-700 px-4 py-2 text-xs font-mono text-gray-300">login.cy.js</div>
                            <div className="p-4 overflow-x-auto">
                                <pre><code>
{`describe('Login Page Test', () => {
  it('should allow a user to log in', () => {
    // 1. Visit the login page
    cy.visit('https://your-demo-website.com/login')

    // 2. Get elements and interact
    cy.get('#username').type('testuser')
    cy.get('#password').type('password123')
    cy.get('form').submit()

    // 3. Verify the result
    cy.url().should('include', '/dashboard')
    cy.contains('Welcome, testuser')
      .should('be.visible')
  });
});`}
                                </code></pre>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </motion.section>

        </div>
    );
};

export default AutomationLearningPage;